import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ASIDE_DESKTOP_WIDTH } from 'constants/settings';
import { breakpoints, screenMd, screenSm } from 'constants/styles/breakpoints';

type LinkProps = { disabled?: boolean; cursor: string };

export const Container = styled.div<{ translateY: string }>`
  position: fixed;
  display: flex;
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  flex-direction: row;
  background-color: ${({ theme }) => theme.default.colors.aside.bg};
  z-index: 900;
  transition: top 0.3s;

  ${screenMd} {
    flex-direction: column;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${ASIDE_DESKTOP_WIDTH + 'px'};
  }

  ${({ translateY }) =>
    translateY &&
    `
    ${screenMd} {
      top: ${translateY}
    }
  `}
`;

export const Logo = styled.div`
  display: none;
  ${screenMd} {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 64px;
  }
`;

export const LogoImage = styled.img`
  width: 92px;
  height: 28px;
  object-fit: contain;
  object-position: center;
`;

export const NavMenu = styled.div<{ $hasCustomContent?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  flex-direction: row;
  padding: 0px 24px;
  height: 70px;
  justify-content: space-around;

  ${({ $hasCustomContent }) =>
    $hasCustomContent &&
    css`
      @media (max-width: ${breakpoints.sm - 1}px) {
        padding: 0px 6px;
        justify-content: space-between;

        > a {
          width: 40px;
          margin: 7px 0px;
        }
      }
    `}

  ${screenSm} {
    min-height: 100%;
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-y: auto;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  ${screenMd} {
    justify-content: center;
    flex-direction: column;
    padding: 0px;
    padding-top: 15px;
    height: auto;
    min-height: auto;
    flex-grow: 1;
    justify-content: start;
  }
`;

export const NavMenuItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 47px;
  height: 45px;
  margin: 7px;
  ${screenSm} {
    width: auto;
    height: auto;
    margin: 0px;
    padding-top: 10px;
  }
  ${screenMd} {
    margin-top: auto;
  }
`;

const linkSharedStyles = css<LinkProps>`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  width: 47px;
  height: 45px;
  margin: 7px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.default.colors.aside.menu.labels};
  cursor: ${({ cursor }) => cursor} !important;
  opacity: 0.7;
  border-radius: 5px;
  transition: opacity 0.4s;

  svg {
    width: 25px;
    height: 25px;
    margin-top: 0px;
    color: ${({ theme }) => theme.default.colors.aside.menu.icons};
  }

  &.selected {
    opacity: 1;
    font-weight: 700;
    background-color: ${({ theme }) => theme.default.colors.aside.menu.bg};
  }

  &:hover,
  &:active,
  &:focus {
    transition: opacity 0.4s;
    opacity: 1;
    background-color: ${({ theme }) => theme.default.colors.aside.menu.bg};
    color: ${({ theme }) => theme.default.colors.aside.menu.labels};
  }
  ${(props) =>
    props.disabled &&
    `
    span {
      color: #57575C;
    }
    svg {
      opacity: 0.3;
    }
  `}

  span {
    display: none;
  }

  .custom-training-label {
    white-space: nowrap;
  }

  ${screenMd} {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    width: 140px;
    height: 35px;
    margin: 0;
    padding: 10px 15px;
    width: 100%;
    border-radius: 0;
    span {
      display: inherit;
    }
    svg {
      height: auto;
      width: 15px;
    }

    &.AI {
      svg {
        width: 28px;
      }
    }

    &.CustomTraining {
      padding-left: 10px;
      gap: 5px;
    }
  }
`;

export const NavMenuLink = styled(NavLink)<LinkProps>`
  ${linkSharedStyles}
`;

export const NavMenuExternalLink = styled.a<LinkProps>`
  ${linkSharedStyles}
`;

export const CustomTrainingAvatar = styled.img`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  border-radius: 110px;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  background: var(--grey-lighter);
  object-fit: cover;
  display: block;
  flex: 0 0 25px;
`;
